import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Space } from 'antd';
import {
  ECellValueType,
  ExcelTableColDef,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import ExcelTable from 'components/ui/excel-table';
import { useQuery } from 'react-query';
import { insuredApi, submissionApi, userApi } from 'services/apis';
import { useNavigate } from 'react-router';
import { RowClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { ENavigationPath } from 'constants/path';
import { SubmissionRow } from './types';
import {
  FilterSubmissionsFieldEnum,
  FilterSubmissionsOrderEnum,
  SubmissionResponseDtoStatusEnum,
  SubmissionResponseDtoTransactionTypeEnum,
} from 'libs/pinnacle-sdk';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import SubmissionStatusIndicator from 'components/ui/submission-status-indicator';
import FilterButton from 'components/ui/filter-button';
import { SortedSelect } from 'components/ui/sorted-select';
import { getLocalDateForMidnightUTC } from 'utils';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';
import usePagination from 'hooks/usePagination';

const SubmissionTable: React.FC = () => {
  const { t } = useTranslation();
  const { submissionTable: savedFilters, setSubmissionTableFilters } =
    useDashboardFiltersStore();

  const [form] = Form.useForm();
  const [sortField, setSortField] = useState<
    FilterSubmissionsFieldEnum | undefined
  >(savedFilters.sortField);
  const [sortOrder, setSortOrder] = useState<
    FilterSubmissionsOrderEnum | undefined
  >(savedFilters.sortOrder);
  const [isFilterOpen, setIsFilterOpen] = useState(savedFilters.isFilterOpen);
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(100);
  const navigate = useNavigate();

  const insuredOptionsMapRef = useRef<Map<string, string>>(new Map());

  // Restore saved filters to form
  useEffect(() => {
    savedFilters.insureds?.forEach((insured) => {
      insuredOptionsMapRef.current.set(insured.value, insured.label);
    });

    form.setFieldsValue({
      insureds: savedFilters.insureds?.map((insured) => insured.value),
      transactionTypes: savedFilters.transactionTypes,
      assignedHandlerIds: savedFilters.assignedHandlerIds,
      submissionStatuses: savedFilters.submissionStatuses,
    });
  }, []);

  const selectedInsureds = Form.useWatch('insureds', form);
  const selectedTransactionTypes = Form.useWatch('transactionTypes', form);
  const selectedAssignedHandlerIds = Form.useWatch('assignedHandlerIds', form);
  const selectedSubmissionStatuses = Form.useWatch('submissionStatuses', form);

  // Save filters to store whenever they change
  useEffect(() => {
    setSubmissionTableFilters({
      insureds:
        selectedInsureds?.map((insuredId: string) => {
          return {
            label: insuredOptionsMapRef.current.get(insuredId) || insuredId,
            value: insuredId,
          };
        }) ?? [],
      transactionTypes: selectedTransactionTypes,
      assignedHandlerIds: selectedAssignedHandlerIds,
      submissionStatuses: selectedSubmissionStatuses,
      sortField,
      sortOrder,
      isFilterOpen,
    });
  }, [
    selectedInsureds,
    selectedTransactionTypes,
    selectedAssignedHandlerIds,
    selectedSubmissionStatuses,
    sortField,
    sortOrder,
    isFilterOpen,
    setSubmissionTableFilters,
  ]);

  useEffect(() => {
    resetPagination();
  }, [
    selectedTransactionTypes,
    selectedAssignedHandlerIds,
    selectedSubmissionStatuses,
    selectedInsureds,
    sortField,
    sortOrder,
    resetPagination,
  ]);

  const { data: submissions, isLoading: isSubmissionsLoading } = useQuery({
    queryKey: [
      currentPage,
      pageSize,
      selectedTransactionTypes,
      selectedAssignedHandlerIds,
      selectedSubmissionStatuses,
      selectedInsureds,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      submissionApi.filterSubmissions({
        page: currentPage,
        pageSize: pageSize,
        field: sortField,
        order: sortOrder,
        filterSubmissionsQueryDto: {
          insureds: selectedInsureds?.length ? selectedInsureds : undefined,
          transactionTypes: selectedTransactionTypes?.length
            ? selectedTransactionTypes
            : undefined,
          assignedHandlerIds: selectedAssignedHandlerIds?.length
            ? selectedAssignedHandlerIds
            : undefined,
          statuses: selectedSubmissionStatuses?.length
            ? selectedSubmissionStatuses
            : undefined,
          includeInsured: true,
          includeUnderwriter: true,
        },
      }),
  });

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        colId: FilterSubmissionsFieldEnum.Insured,
        field: 'insured',
        headerName: t('insured.insured'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterSubmissionsFieldEnum.TransactionType,
        field: 'transactionType',
        headerName: t('submission.transactionType'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'lineOfBusiness',
        headerName: t('submission.lineOfBusiness'),
        minWidth: 100,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'submissionType',
        headerName: t('submission.policyType'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'mainRegionAndRiskTerritory',
        headerName: t('submission.mainRegionAndRiskTerritory'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterSubmissionsFieldEnum.Underwriter,
        field: 'assignedHandler',
        headerName: t('submission.assignedHandler'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        field: 'namedUnderwriter',
        headerName: t('submission.namedUnderwriter'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'inceptionDate',
        headerName: t('submission.inceptionDate'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'expiryDate',
        headerName: t('submission.expiryDate'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'totalInsurableValue',
        headerName: t('submission.totalInsurableValue'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
        resizable: true,
        editable: false,
      },
      {
        colId: FilterSubmissionsFieldEnum.SubmissionStatus,
        field: 'submissionStatus',
        headerName: t('submission.submissionStatus'),
        minWidth: 180,
        flex: 1,
        resizable: true,
        editable: false,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        cellClass: 'ag-center-cell',
        cellRenderer: (params: { value: SubmissionResponseDtoStatusEnum }) => (
          <SubmissionStatusIndicator status={params.value} />
        ),
      },
    ],
    [t]
  );

  const rowData: SubmissionRow[] = useMemo(() => {
    return (
      submissions?.items.map((item, index) => ({
        insured: item.insured?.name,
        submissionType: item.policyType,
        lineOfBusiness: item.lineOfBusiness,
        transactionType: item.transactionType,
        mainRegionAndRiskTerritory: item.mainRegionAndRiskTerritory,
        assignedHandler: item.assignedHandler?.name,
        namedUnderwriter: item.namedUnderwriter?.name,
        inceptionDate: getLocalDateForMidnightUTC(item.inceptionDate).format(
          'DD/MM/YYYY'
        ),
        expiryDate: item.expiryDate
          ? getLocalDateForMidnightUTC(item.expiryDate).format('DD/MM/YYYY')
          : undefined,
        totalInsurableValue: item.totalTivView?.totalTiv || '0',
        submissionStatus: item.status,
        submissionId: item.submissionId,
        id: pageSize * (currentPage - 1) + index + 1,
      })) || []
    );
  }, [currentPage, pageSize, submissions?.items]);

  const handleRowClick = useCallback(
    (row: RowClickedEvent) => {
      navigate(
        ENavigationPath.SUBMISSION_DETAIL.replace(
          ':submissionId',
          row.data.submissionId
        ).replace(':tabId', '0')
      );
    },
    [navigate]
  );

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    const sortedCol = event.api.getColumnState().find((col) => col.sort);
    if (sortedCol) {
      setSortField(sortedCol.colId as FilterSubmissionsFieldEnum);
      setSortOrder(
        sortedCol.sort === 'asc'
          ? FilterSubmissionsOrderEnum.Asc
          : FilterSubmissionsOrderEnum.Desc
      );
    } else {
      setSortField(undefined);
      setSortOrder(undefined);
    }
  }, []);

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Form form={form} className="w-full">
        <div className="flex items-center justify-end mb-6">
          <div className="flex gap-2">
            <FilterButton
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
          </div>
        </div>
        {isFilterOpen && (
          <Space direction="horizontal" wrap>
            <Form.Item name="insureds" className="min-w-[220px] my-2">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('insured.selectInsured')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await insuredApi.filterInsureds({
                    page,
                    pageSize,
                    insuredFilterQueryDto: {
                      searchText,
                    },
                  });
                  const options = items.map((insured) => ({
                    label: insured.name,
                    value: insured.insuredId,
                  }));
                  options.forEach((option) => {
                    insuredOptionsMapRef.current.set(
                      option.value,
                      option.label
                    );
                  });
                  return {
                    data: options,
                  };
                }}
                additionalOptions={savedFilters.insureds}
                maxTagCount={1}
                size="large"
                allowClear
                queryKey="insured-lazy-loading-select"
              />
            </Form.Item>

            <Form.Item name="transactionTypes" className="min-w-[220px] my-2">
              <SortedSelect
                mode="multiple"
                placeholder={t('submission.selectTransactionType')}
                options={Object.values(
                  SubmissionResponseDtoTransactionTypeEnum
                ).map((name) => ({
                  label: name,
                  value: name,
                }))}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                className="rounded-xl"
              />
            </Form.Item>

            <Form.Item name="assignedHandlerIds" className="min-w-[220px] my-2">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('submission.selectUnderwriter')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await userApi.filterUsers({
                    searchText,
                    page,
                    pageSize,
                  });
                  return {
                    data: items.map((user) => ({
                      label: user.name || '',
                      value: user.userId,
                    })),
                  };
                }}
                additionalOptions={[]}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                showSearch
                queryKey="underwriter-lazy-loading-select"
              />
            </Form.Item>

            <Form.Item name="submissionStatuses" className="min-w-[220px] my-2">
              <SortedSelect
                mode="multiple"
                placeholder={t('submission.selectSubmissionStatus')}
                options={Object.values(SubmissionResponseDtoStatusEnum).map(
                  (name) => ({
                    label: <SubmissionStatusIndicator status={name} />,
                    value: name,
                  })
                )}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                className="rounded-xl"
              />
            </Form.Item>
          </Space>
        )}
      </Form>
      <ExcelTable
        data={rowData}
        columnDefs={columnDefs}
        onRowClicked={handleRowClick}
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        onSortChanged={handleSortChanged}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={submissions?.pagination?.total || 0}
        tableContainerClassName="h-[28rem]"
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        loading={isSubmissionsLoading}
      />
    </div>
  );
};

export default SubmissionTable;
